import { LatLng, LatLngTuple } from 'leaflet';
import {
  Coordinate,
  SimpleMarkerData,
  DeviceNodePolyline,
  ActiveNodePosition,
  LegMarker,
  LegMarkerKind,
  TransformedLocationData,
} from '@features/components/LocationTracking/types';
import { DeviceCoordinate, DeviceNodeType, DeviceTracking } from '@/api/apis/composer';
import { getCurvedPolyline } from '@/utils/helpers/route-map.helpers';
import { decodePolyline } from './polyline.utils';
import { getLegState, isLegComplete, LegState } from './legState.utils';

const isValidCoordinate = (coordinate: DeviceCoordinate | null | undefined): coordinate is Coordinate =>
  coordinate?.latitude != null && coordinate?.longitude != null;

const buildAirFallbackPolyline = (departureCoordinate: Coordinate, arrivalCoordinate: Coordinate): LatLngTuple[] => {
  const start = new LatLng(departureCoordinate.latitude, departureCoordinate.longitude);
  const end = new LatLng(arrivalCoordinate.latitude, arrivalCoordinate.longitude);
  return getCurvedPolyline(start, end).map((p) => [p.lat, p.lng] as LatLngTuple);
};

const buildGroundFallbackPolyline = (departure: Coordinate, arrival: Coordinate): LatLngTuple[] => [
  [departure.latitude, departure.longitude],
  [arrival.latitude, arrival.longitude],
];

type DeviceNode = DeviceTracking['deviceNodes'][number];

const findActiveLeg = (nodes: DeviceNode[]): DeviceNode | null => {
  for (let i = 0; i < nodes.length; i += 1) {
    if (getLegState(nodes[i], i, nodes) === LegState.InProgress) {
      return nodes[i];
    }
  }
  return null;
};

const findLastCompletedLegWithPosition = (nodes: DeviceNode[]): DeviceNode | null => {
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const node = nodes[i];
    const state = getLegState(node, i, nodes);
    if (isLegComplete(state) && node.currentPosition && isValidCoordinate(node.currentPosition.coordinate)) {
      return node;
    }
  }
  return null;
};

const toActivePosition = (node: DeviceNode, isActive: boolean): ActiveNodePosition | null => {
  const pos = node.currentPosition;
  if (!pos || !isValidCoordinate(pos.coordinate)) return null;
  return {
    location: new LatLng(pos.coordinate.latitude!, pos.coordinate.longitude!),
    heading: pos.heading,
    nodeType: node.nodeType,
    timestamp: pos.timestamp,
    isActive,
  };
};

const buildActivePosition = (nodes: DeviceNode[]): ActiveNodePosition | null => {
  const active = findActiveLeg(nodes);
  if (active) {
    const position = toActivePosition(active, true);
    if (position) return position;
    // Active leg is missing live coordinates — hide the moving marker instead of breaking the map.
    // eslint-disable-next-line no-console
    console.warn('[LocationTracking] in_progress leg without currentPosition', { nodeId: active.nodeId });
  }
  const lastCompleted = findLastCompletedLegWithPosition(nodes);
  return lastCompleted ? toActivePosition(lastCompleted, false) : null;
};

export const transformLocationTrackingData = (
  data: DeviceTracking | undefined,
  fallbackDeparture?: Coordinate | null,
  fallbackArrival?: Coordinate | null,
  groundRouteFallbacks?: Map<string, LatLngTuple[]>,
): TransformedLocationData | null => {
  if (!data?.deviceNodes?.length) {
    return null;
  }

  const firstNode = data.deviceNodes[0];
  const lastNode = data.deviceNodes[data.deviceNodes.length - 1];

  const routeDeparture = firstNode.departureCheckpoint?.coordinate;
  const routeArrival = lastNode.arrivalCheckpoint?.coordinate;

  const resolvedDeparture = isValidCoordinate(routeDeparture) ? routeDeparture : fallbackDeparture;
  const resolvedArrival = isValidCoordinate(routeArrival) ? routeArrival : fallbackArrival;

  if (!resolvedDeparture || !resolvedArrival) {
    return null;
  }

  const donorHospitalMarker: SimpleMarkerData = {
    id: `departure-${firstNode.legId}`,
    location: new LatLng(resolvedDeparture.latitude, resolvedDeparture.longitude),
  };

  const txCenterMarker: SimpleMarkerData = {
    id: `arrival-${lastNode.legId}`,
    location: new LatLng(resolvedArrival.latitude, resolvedArrival.longitude),
  };

  const legMarkers: LegMarker[] = [];
  const devicePolylines: DeviceNodePolyline[] = [];

  data.deviceNodes.forEach((node, index) => {
    const departureCoordinate = node.departureCheckpoint?.coordinate;
    const arrivalCoordinate = node.arrivalCheckpoint?.coordinate;

    const isFirstNode = index === 0;
    const isLastNode = index === data.deviceNodes.length - 1;

    const legDeparture = isValidCoordinate(departureCoordinate) ? departureCoordinate : null;
    const legArrival = isValidCoordinate(arrivalCoordinate) ? arrivalCoordinate : null;

    const resolvedLegDeparture = legDeparture ?? (isFirstNode ? resolvedDeparture : null);
    const resolvedLegArrival = legArrival ?? (isLastNode ? resolvedArrival : null);

    const legState = getLegState(node, index, data.deviceNodes);
    const isComplete = isLegComplete(legState);

    if (resolvedLegArrival) {
      if (isComplete) {
        legMarkers.push({
          kind: LegMarkerKind.CompletedArrival,
          id: `leg-arrival-${node.legId}`,
          location: new LatLng(resolvedLegArrival.latitude, resolvedLegArrival.longitude),
          nodeType: node.nodeType,
        });
      } else if (!isLastNode) {
        // The final destination already has its own marker, so do not add a duplicate dot there.
        legMarkers.push({
          kind: LegMarkerKind.PendingHandoff,
          id: `leg-arrival-${node.legId}`,
          location: new LatLng(resolvedLegArrival.latitude, resolvedLegArrival.longitude),
        });
      }
    }

    let plannedPositions = decodePolyline(node.plannedPolyline?.polyline ?? null);
    const trackedPositions = decodePolyline(node.trackedPolyline?.polyline ?? null);

    if (
      node.nodeType === DeviceNodeType.Air &&
      plannedPositions.length === 0 &&
      resolvedLegDeparture &&
      resolvedLegArrival
    ) {
      plannedPositions = buildAirFallbackPolyline(resolvedLegDeparture, resolvedLegArrival);
    }

    if (
      node.nodeType === DeviceNodeType.Ground &&
      plannedPositions.length === 0 &&
      trackedPositions.length === 0 &&
      resolvedLegDeparture &&
      resolvedLegArrival
    ) {
      const hereRoute = groundRouteFallbacks?.get(node.nodeId);
      const fallback =
        hereRoute && hereRoute.length > 1
          ? hereRoute
          : buildGroundFallbackPolyline(resolvedLegDeparture, resolvedLegArrival);

      if (legState === LegState.StaleCompleted) {
        trackedPositions.push(...fallback);
      } else {
        plannedPositions = fallback;
      }
    }

    if (
      trackedPositions.length > 0 &&
      plannedPositions.length === 0 &&
      !isComplete &&
      node.currentPosition &&
      isValidCoordinate(node.currentPosition.coordinate)
    ) {
      const lastTracked = trackedPositions.at(-1);
      const currentLat = node.currentPosition.coordinate.latitude;
      const currentLng = node.currentPosition.coordinate.longitude;
      if (lastTracked && (lastTracked[0] !== currentLat || lastTracked[1] !== currentLng)) {
        trackedPositions.push([currentLat, currentLng]);
      }
    }

    const resolvedDepartureTime = node.departureTime.atd ?? node.departureTime.ptd ?? node.departureTime.std;
    const resolvedArrivalTime = node.arrivalTime.eta ?? node.arrivalTime.sta ?? node.arrivalTime.pta;

    devicePolylines.push({
      id: node.nodeId,
      nodeType: node.nodeType,
      plannedPositions,
      trackedPositions,
      isComplete,
      departureTime: resolvedDepartureTime,
      arrivalTime: resolvedArrivalTime,
    });
  });

  const activePosition = buildActivePosition(data.deviceNodes);

  return {
    donorHospitalMarker,
    txCenterMarker,
    locations: [],
    legs: [],
    devicePolylines,
    legMarkers,
    activePosition,
  };
};
